<template>
  <Teleport to="body">
    <div v-if="open && activeStep" class="creator-tour" @keydown="handleKeydown">
      <template v-if="spotlight">
        <div class="creator-tour-shield" />
        <div class="creator-tour-focus" :style="spotlightStyle" />
      </template>
      <div v-else class="creator-tour-curtain creator-tour-curtain-full" />

      <div
        ref="dialogRef"
        role="dialog"
        aria-modal="true"
        aria-labelledby="creator-tour-title"
        tabindex="-1"
        class="creator-tour-card"
        :style="cardStyle"
      >
        <div class="creator-tour-card-top">
          <div class="flex items-center gap-2">
            <span class="creator-tour-sparkle" aria-hidden="true">✦</span>
            <span class="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-700">Your Platforma guide</span>
          </div>
          <button type="button" class="creator-tour-close" aria-label="Close setup guide" @click="$emit('close')">×</button>
        </div>

        <div class="creator-tour-card-body px-6 pb-6 pt-5 sm:px-7">
          <div class="flex items-center justify-between gap-3 text-xs font-semibold text-slate-500">
            <span>Step {{ activeIndex + 1 }} of {{ steps.length }}</span>
            <span v-if="activeStep.done" class="rounded-full bg-emerald-50 px-2.5 py-1 text-emerald-700">Already done</span>
          </div>
          <div class="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100" aria-hidden="true">
            <div class="h-full rounded-full bg-gradient-to-r from-blue-500 via-indigo-500 to-cyan-400 transition-[width] duration-300" :style="{ width: progressWidth }" />
          </div>
          <h2 id="creator-tour-title" class="mt-6 text-2xl font-semibold tracking-tight text-slate-950">{{ activeStep.title }}</h2>
          <p class="mt-2 text-sm leading-6 text-slate-600">{{ activeStep.description }}</p>

          <div v-if="activeStep.to" class="mt-5 rounded-2xl border border-blue-100 bg-blue-50/70 p-3.5 text-xs leading-5 text-blue-900">
            You can open this area now, or continue the tour first.
          </div>

          <div class="mt-6 flex flex-wrap items-center justify-between gap-3">
            <button type="button" class="text-xs font-semibold text-slate-500 transition hover:text-slate-900" @click="$emit('close')">Skip guide</button>
            <div class="flex flex-wrap justify-end gap-2">
              <button v-if="activeIndex > 0" type="button" class="creator-tour-secondary" @click="activeIndex--">Back</button>
              <button v-if="activeStep.to" type="button" class="creator-tour-secondary" @click="$emit('navigate', activeStep.to)">{{ activeStep.action || 'Open area' }}</button>
              <button type="button" class="creator-tour-primary" @click="advance">{{ activeIndex === steps.length - 1 ? 'Finish tour' : 'Next' }} <span aria-hidden="true">→</span></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';

const props = defineProps({
  open: { type: Boolean, default: false },
  steps: { type: Array, default: () => [] },
});
const emit = defineEmits(['close', 'finish', 'navigate']);
const activeIndex = ref(0);
const dialogRef = ref(null);
const spotlight = ref(null);
const cardStyle = ref({});
let measureFrame = 0;

const activeStep = computed(() => props.steps[activeIndex.value] || null);
const progressWidth = computed(() => `${((activeIndex.value + 1) / Math.max(1, props.steps.length)) * 100}%`);
const spotlightStyle = computed(() => spotlight.value ? {
  top: `${spotlight.value.top}px`, left: `${spotlight.value.left}px`,
  width: `${spotlight.value.width}px`, height: `${spotlight.value.height}px`,
} : {});
function findTarget() {
  for (const selector of activeStep.value?.targets || []) {
    for (const target of document.querySelectorAll(selector)) {
      if (!target.getClientRects().length) continue;
      const style = window.getComputedStyle(target);
      const bounds = target.getBoundingClientRect();
      if (style.display === 'none' || style.visibility === 'hidden') continue;
      if (bounds.width < 1 || bounds.height < 1) continue;
      const isNavigationTarget = ['dashboard-nav', 'theme-editor', 'membership-settings'].includes(target.getAttribute('data-creator-tour'));
      if (isNavigationTarget && (bounds.bottom <= 8 || bounds.top >= window.innerHeight - 8 || bounds.right <= 8 || bounds.left >= window.innerWidth - 8)) continue;
      return target;
    }
  }
  return null;
}

function measure() {
  if (!props.open || !activeStep.value) return;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const cardWidth = Math.min(390, viewportWidth - 32);
  const cardHeight = dialogRef.value?.offsetHeight || 330;
  const target = findTarget();
  if (!target) {
    spotlight.value = null;
    cardStyle.value = { width: `${cardWidth}px`, left: `${(viewportWidth - cardWidth) / 2}px`, top: `${Math.max(16, (viewportHeight - cardHeight) / 2)}px` };
    return;
  }

  const bounds = target.getBoundingClientRect();
  const left = Math.max(8, bounds.left - 8);
  const top = Math.max(8, bounds.top - 8);
  const right = Math.min(viewportWidth - 8, bounds.right + 8);
  const bottom = Math.min(viewportHeight - 8, bounds.bottom + 8);
  spotlight.value = { left, top, width: Math.max(0, right - left), height: Math.max(0, bottom - top) };

  let cardLeft;
  let cardTop;
  if (right + cardWidth + 28 < viewportWidth) {
    cardLeft = right + 18;
    cardTop = top;
  } else if (left - cardWidth - 28 > 0) {
    cardLeft = left - cardWidth - 18;
    cardTop = top;
  } else {
    cardLeft = Math.max(16, Math.min(viewportWidth - cardWidth - 16, left));
    cardTop = bottom + cardHeight + 22 < viewportHeight ? bottom + 14 : top - cardHeight - 14;
  }
  cardStyle.value = {
    width: `${cardWidth}px`,
    left: `${Math.max(16, Math.min(viewportWidth - cardWidth - 16, cardLeft))}px`,
    top: `${Math.max(16, Math.min(viewportHeight - cardHeight - 16, cardTop))}px`,
  };
}

function scheduleMeasure() {
  if (measureFrame) cancelAnimationFrame(measureFrame);
  measureFrame = requestAnimationFrame(() => { measureFrame = 0; measure(); });
}

async function showStep() {
  await nextTick();
  if (!props.open) return;
  const target = findTarget();
  const inNavigation = ['dashboard-nav', 'theme-editor', 'membership-settings'].includes(target?.getAttribute('data-creator-tour'));
  if (target && !inNavigation) target.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
  scheduleMeasure();
  dialogRef.value?.focus({ preventScroll: true });
}

function advance() {
  if (activeIndex.value < props.steps.length - 1) activeIndex.value++;
  else emit('finish');
}

function handleKeydown(event) {
  if (event.key === 'Escape') { event.preventDefault(); emit('close'); return; }
  if (event.key !== 'Tab') return;
  const buttons = Array.from(dialogRef.value?.querySelectorAll('button:not([disabled])') || []);
  if (!buttons.length) return;
  const first = buttons[0];
  const last = buttons[buttons.length - 1];
  if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.value)) { event.preventDefault(); last.focus(); }
  else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialogRef.value)) { event.preventDefault(); first.focus(); }
}

watch(() => props.open, (open) => { if (open) { activeIndex.value = 0; void showStep(); } else spotlight.value = null; });
watch(activeIndex, () => { if (props.open) void showStep(); });
onMounted(() => {
  window.addEventListener('resize', scheduleMeasure);
  window.addEventListener('scroll', scheduleMeasure, true);
});
onUnmounted(() => {
  window.removeEventListener('resize', scheduleMeasure);
  window.removeEventListener('scroll', scheduleMeasure, true);
  if (measureFrame) cancelAnimationFrame(measureFrame);
});
</script>

<style scoped>
.creator-tour { position: fixed; inset: 0; z-index: 2100; font-family: inherit; }
.creator-tour-shield { position: fixed; inset: 0; z-index: 0; }
.creator-tour-curtain { position: fixed; z-index: 0; background: rgba(10, 22, 43, .44); }
.creator-tour-curtain-full { inset: 0; }
.creator-tour-focus { position: fixed; z-index: 1; pointer-events: none; border: 2px solid rgba(255,255,255,.96); border-radius: 20px; box-shadow: 0 0 0 5px rgba(45,140,255,.45), 0 0 0 200vmax rgba(10,22,43,.44); }
.creator-tour-card { position: fixed; z-index: 2; max-height: calc(100dvh - 32px); overflow-y: auto; border: 1px solid rgba(255,255,255,.94); border-radius: 24px; background: transparent; box-shadow: 0 30px 90px rgba(12,31,70,.28), 0 5px 20px rgba(12,31,70,.10); outline: none; }
.creator-tour-card-top { display: flex; align-items: center; justify-content: space-between; padding: 17px 20px; background: linear-gradient(110deg, rgba(252, 252, 252, 0.082), rgba(223,215,255,.38) 48%, rgba(208,247,252,.66)), #fff; border-bottom: 1px solid rgba(221,233,250,.8); }
.creator-tour-card-body { background: rgb(255, 255, 255); backdrop-filter: blur(26px) saturate(1.25); -webkit-backdrop-filter: blur(26px) saturate(1.25); }
.creator-tour-sparkle { display: grid; width: 26px; height: 26px; place-items: center; border-radius: 9px; background: #2d8cff; color: white; font-size: 17px; box-shadow: 0 4px 12px rgba(45,140,255,.25); }
.creator-tour-close { display: grid; width: 30px; height: 30px; place-items: center; border-radius: 9px; color: #64748b; font-size: 24px; line-height: 1; transition: background .15s, color .15s; }
.creator-tour-close:hover { background: rgba(255,255,255,.7); color: #0f172a; }
.creator-tour-primary, .creator-tour-secondary { display: inline-flex; min-height: 38px; align-items: center; justify-content: center; gap: 6px; border-radius: 11px; padding: 8px 12px; font-size: 12px; font-weight: 700; transition: transform .15s, background .15s, box-shadow .15s; }
.creator-tour-primary { background: #2d8cff; color: white; box-shadow: 0 5px 14px rgba(45,140,255,.24); }
.creator-tour-primary:hover { background: #176fe2; transform: translateY(-1px); }
.creator-tour-secondary { border: 1px solid #dbe4ef; background: white; color: #334155; }
.creator-tour-secondary:hover { background: #f8fbff; }
</style>
